/**
 * The member order every split / paid-by screen lists (mobile parity):
 *   1. the signed-in user ("You") first,
 *   2. then members with a real name, A → Z (case-insensitive),
 *   3. then phone-only members (no name — the phone number stands in), by number.
 *
 * This is not cosmetic: the split engine's remainder rules are ORDER-based ("last eligible
 * absorbs", equal-split leftover cents go to the last ids), so a different member order than
 * mobile's gives different per-member amounts for the very same Split by amount / percentage.
 */
export interface OrderableMember {
  readonly id: string;
  readonly name: string;
  readonly phone: string;
  readonly isYou: boolean;
}

/** A display name that is really a phone number (the no-name fallback): digits, +, spaces, dashes. */
const PHONE_LIKE = /^\+?[\d\s()-]+$/;

export function isPhoneOnlyMember(member: Pick<OrderableMember, 'name' | 'phone'>): boolean {
  const name = member.name.trim();
  return name === '' || (member.phone !== '' && name === member.phone) || PHONE_LIKE.test(name);
}

const byName = new Intl.Collator(undefined, { sensitivity: 'base' });
const byNumber = new Intl.Collator(undefined, { numeric: true });

/** Stable: members that compare equal keep their incoming relative order. */
export function sortSplitMembers<T extends OrderableMember>(members: readonly T[]): T[] {
  const rank = (m: T): number => (m.isYou ? 0 : isPhoneOnlyMember(m) ? 2 : 1);
  return [...members].sort((a, b) => {
    const ra = rank(a);
    const rb = rank(b);
    if (ra !== rb) return ra - rb;
    if (ra === 1) return byName.compare(a.name.trim(), b.name.trim());
    if (ra === 2) return byNumber.compare(a.name.replace(/\D/g, ''), b.name.replace(/\D/g, ''));
    return 0;
  });
}
